import type { HoverContent } from "./HoverCard";
import { polyline, ticks } from "./scale";

export interface Series {
  values: (number | null)[];
  color: string;
  label: string;
}

/**
 * One or more line series on a labelled, **chosen** y-axis.
 *
 * Used by the retention trend, which is the only genuine time-series chart in Stats — everything
 * else is bars. Two series, young and mature, over the full history.
 *
 * **The domain is a prop and must stay one.** Retention runs 60–100% because that is the range the
 * question lives in; auto-fitting it would rescale the axis every time the data moved, so a flat
 * year would render as a crisis and a real collapse would render as a gentle slope. The same
 * argument is why `ticks` divides the domain it is given rather than choosing a "nicer" one.
 *
 * A `null` in a series is a **gap**, not a zero — a 30-day window with too few reviews to have a
 * rate. `polyline` breaks the line there rather than drawing it to the floor, which would read as a
 * month of total failure.
 */
export function Line({
  series,
  domain,
  height = 120,
  fill = false,
  target,
  tickCount = 4,
  format = (v: number) => `${v}%`,
  hoverAt,
  onEnter,
  onLeave,
}: {
  series: Series[];
  domain: [number, number];
  height?: number;
  /**
   * Grow to fill the panel instead of standing at `height` — see `Bars`' note. The plot is already
   * a `viewBox` with `preserveAspectRatio="none"` and every rule is positioned as a percentage, so
   * nothing inside this component knows or cares how tall it ends up; the two fixed heights on the
   * wrappers were the only thing pinning it.
   */
  fill?: boolean;
  /** A horizontal dashed rule — FSRS's desired retention on the trend. */
  target?: number;
  tickCount?: number;
  format?: (value: number) => string;
  /** What the card says about one sample. `null` for a sample with nothing to say — a window with
   *  too few reviews to have a rate, where the line itself is a gap. */
  hoverAt?: (index: number) => HoverContent | null;
  onEnter?: (event: { currentTarget: Element }, content: HoverContent) => void;
  onLeave?: () => void;
}) {
  const [low, high] = domain;
  const span = high - low || 1;
  const axis = ticks(low, high, tickCount);
  // A viewBox with `preserveAspectRatio="none"` is what makes this responsive without a
  // ResizeObserver: the SVG stretches to its container and the maths stays in one unit system.
  const width = 1000;

  return (
    <div
      className="flex gap-[8px]"
      style={fill ? { flex: "1 1 auto", minHeight: height } : undefined}
    >
      {/* Both children stretch to the row on their own — `align-items` defaults to `stretch` — so
          filling is a matter of NOT pinning them, rather than of sizing them. */}
      <div
        className="flex shrink-0 flex-col justify-between text-meta text-ink-4"
        style={fill ? undefined : { height }}
      >
        {[...axis].reverse().map((value) => (
          <span key={value} className="font-mono leading-none">
            {format(value)}
          </span>
        ))}
      </div>
      <div className="relative min-w-0 flex-1" style={fill ? undefined : { height }}>
        {axis.map((value) => (
          <span
            key={value}
            aria-hidden
            className="absolute inset-x-0 h-px"
            style={{
              bottom: `${((value - low) / span) * 100}%`,
              background: "var(--chart-grid)",
            }}
          />
        ))}
        {target !== undefined && target >= low && target <= high && (
          <span
            aria-hidden
            className="absolute inset-x-0 h-0 border-t border-dashed"
            style={{
              bottom: `${((target - low) / span) * 100}%`,
              borderColor: "var(--trend-target)",
            }}
          />
        )}
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          role="img"
          // The chart's whole accessible name — the series labels are Japanese chrome, so the
          // glue between them has to be too, or the one English word on the surface is the one
          // nobody can see.
          aria-label={series.map((s) => s.label).join("と")}
        >
          {/* **One `<polyline>` per unbroken run, not one per series.** Omitting a gap's point
              from a single polyline does not break the line — SVG joins the points either side of
              it — so a stretch with no rate rendered as a straight, confident segment through a
              period the data says nothing about. See `polyline`. */}
          {series.map((line) =>
            polyline(line.values, { width, height, domain }).map((points, run) => (
              <polyline
                key={`${line.label}-${run}`}
                points={points}
                fill="none"
                stroke={line.color}
                strokeWidth={2}
                // The viewBox is stretched horizontally, so a stroke scaled with it would render
                // several pixels thick. This keeps 2px meaning 2px at any width.
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
              />
            )),
          )}
        </svg>

        {/* **Hit zones, because a stroke cannot be hovered.** One invisible full-height column per
            sample, laid over the chart; the column under the pointer draws a crosshair from a
            `:hover` rule rather than from state, and the card reads every series at that index.
            `flex-1` rather than a computed left offset so the columns track the SVG's own stretch.
            Rendered last so they sit above the lines. */}
        {hoverAt && onEnter && (
          <div className="absolute inset-0 flex">
            {series[0]?.values.map((_, index) => {
              const content = hoverAt(index);
              return (
                <span
                  key={index}
                  className={`h-full min-w-px flex-1 ${content ? "chart-column" : ""}`}
                  onMouseEnter={content ? (event) => onEnter(event, content) : undefined}
                  onMouseLeave={content ? onLeave : undefined}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** The key for the lines above — colour, name, and the current figure where there is one. */
export function LineLegend({ items }: { items: { label: string; color: string; value?: string }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-[14px]">
      {items.map(({ label, color, value }) => (
        <span key={label} className="flex items-center gap-[5px] text-meta text-ink-3">
          <span aria-hidden className="h-[2px] w-[12px]" style={{ background: color }} />
          {label}
          {value && <span className="font-mono text-ink">{value}</span>}
        </span>
      ))}
    </div>
  );
}
